import { useMemo, useState } from 'react';
import { Button, Checkbox, Drawer, Radio } from '@arco-design/web-react';
import { BccTabPane, BccTabs, DRAWER_WIDTH } from '../../components';
import { DEFAULT_THEME_KEY, THEMES, type ThemeDef } from '../../theme/themes';
import { BORDER_LEVELS, DEFAULT_BORDER_LEVEL } from '../../theme/borders';
import { flatNav } from '../../registry/features';
import {
  DEFAULT_DENSITY,
  DEFAULT_LANDING,
  DEFAULT_MENU_EXPAND,
  DEFAULT_MOTION,
  DEFAULT_RADIUS,
  DEFAULT_TIME_FMT,
  DEFAULT_WIDTH,
  DENSITY_LEVELS,
  LANDING_LEVELS,
  MENU_EXPAND_LEVELS,
  MOTION_LEVELS,
  RADIUS_LEVELS,
  TIME_FMT_LEVELS,
  WIDTH_LEVELS,
} from '../../theme/uiPrefs';
import { useThemeStore } from '../../store/theme';
import {
  DEFAULT_RING_ENABLED,
  DEFAULT_RING_VOLUME,
  RING_ENABLED_LEVELS,
  RING_VOLUME_LEVELS,
  getRingEnabledLevel,
  getRingVolumeLevel,
} from '../../utils/ringPrefs';

/** 主题色带：主色 → 渐变终点 → AI 色 → AI 浅档 的一条连续渐变（色值即主题数据，故行内下发） */
function themeStrip(t: ThemeDef) {
  return {
    backgroundImage: `linear-gradient(90deg, ${t.primary} 0%, ${t.gradTo} 42%, ${t.ai} 76%, ${t.aiLight} 100%)`,
  };
}

/** 一条界面偏好：标题在左、分段控件在右（三组等宽），说明独占下一行 */
function PrefRow({
  title,
  value,
  options,
  desc,
  onChange,
}: {
  title: string;
  value: string;
  options: { key: string; name: string }[];
  desc: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="bcc-pref-row">
      <div className="bcc-pref-head">
        <span className="bcc-pref-title">{title}</span>
        <Radio.Group
          className="bcc-pref-seg"
          type="button"
          size="small"
          value={value}
          onChange={(v) => onChange(String(v))}
        >
          {options.map((o) => (
            <Radio key={o.key} value={o.key}>
              {o.name}
            </Radio>
          ))}
        </Radio.Group>
      </div>
      <div className="bcc-pref-desc">{desc}</div>
    </div>
  );
}

/**
 * 界面设置抽屉（入口：侧栏底栏「设置」；实例只挂一处 = layouts/BasicLayout）。
 * 四页：主题配色 / 线框样式 / 界面偏好 / 提醒。
 *
 * 排版口径（修改前先读，别把密度退回去）：
 * - **一屏内可完成选择**：主题用「单行紧凑列表」（单选点 + 名称 + 色带），10 套一屏看完，
 *   不再纵向铺 10 张百来像素的卡；场景文案只在列表下方为**当前选中项**显示一行，不逐行重复。
 * - **一条设置 = 标题 + 控件同行 + 说明在下一行**：标题占满左侧余宽、控件定宽右对齐（`.bcc-pref-seg`），
 *   因此选项数不同的三组（4 / 3 / 3）也等宽对齐；说明**只写一遍**，不再"标题旁一句 + 控件下再说一句"。
 * - **同类设置只用一种控件**：短枚举一律用分段按钮（`Radio.Group type="button"`），不混搭卡片列表。
 * - **页签已是标题**：面板内不再重复写一遍"主题配色 / 线框样式"，只留一行补充说明。
 * - **左基线只有一条**（抽屉正文 16px）：页签首项与内容左缘对齐（`headerPadding={false}`），
 *   主题行的单选点收进卡片内，不再出现"标题 16 / 页签 32 / 卡片 38"三条竖线。
 * - 间距只用 8 / 12 / 16（`--bcc-space-*` 档位）；字号只用 13（标题 / 主行）/ 12（说明）/ 11（弱化 meta）；
 *   说明分两档：选项目说明（`--bcc-text-2`）重、页脚登记说明（`--bcc-text-3`）弱。
 * - 色带是一条主色 → AI 色渐变：相邻主题的第 2 / 4 色常重复、末格贴近白底，
 *   分格既看不出差异、又像"没填满的进度条"。
 * - 色值与数值由 `themes.ts` / `borders.ts` / `uiPrefs.ts` 数据渲染（色值即主题数据，故以行内样式下发）；
 *   结构性样式走 `.bcc-theme-*` / `.bcc-pref-*` 全局类（styles/components.css），不新增 `.arco-*` 覆盖。
 * - **只放用户做选择需要的信息**：`themes.ts` 的 `caveat` 是内部登记项，仅供文档与体检脚本使用，**不上界面**。
 * 切换即时生效、无需刷新：通话中的工作台不会因换主题中断，图表色在页面重新挂载后跟随。
 */
export default function ThemeDrawer() {
  const key = useThemeStore((s) => s.key);
  const setTheme = useThemeStore((s) => s.setTheme);
  const border = useThemeStore((s) => s.border);
  const setBorder = useThemeStore((s) => s.setBorder);
  const width = useThemeStore((s) => s.width);
  const setWidth = useThemeStore((s) => s.setWidth);
  const motion = useThemeStore((s) => s.motion);
  const setMotion = useThemeStore((s) => s.setMotion);
  const radius = useThemeStore((s) => s.radius);
  const setRadius = useThemeStore((s) => s.setRadius);
  const density = useThemeStore((s) => s.density);
  const setDensity = useThemeStore((s) => s.setDensity);
  const landing = useThemeStore((s) => s.landing);
  const setLanding = useThemeStore((s) => s.setLanding);
  const menuExpand = useThemeStore((s) => s.menuExpand);
  const setMenuExpand = useThemeStore((s) => s.setMenuExpand);
  const menuPinned = useThemeStore((s) => s.menuPinned);
  const setMenuPinned = useThemeStore((s) => s.setMenuPinned);
  const timeFmt = useThemeStore((s) => s.timeFmt);
  const setTimeFmt = useThemeStore((s) => s.setTimeFmt);
  const ringEnabled = useThemeStore((s) => s.ringEnabled);
  const setRingEnabled = useThemeStore((s) => s.setRingEnabled);
  const ringVolume = useThemeStore((s) => s.ringVolume);
  const setRingVolume = useThemeStore((s) => s.setRingVolume);
  const visible = useThemeStore((s) => s.drawerVisible);
  const closeDrawer = useThemeStore((s) => s.closeDrawer);
  const [tab, setTab] = useState('theme');
  const current = THEMES.find((t) => t.key === key) ?? THEMES[0];
  const borderLevel = BORDER_LEVELS.find((lv) => lv.key === border) ?? BORDER_LEVELS[0];
  const radiusLevel = RADIUS_LEVELS.find((lv) => lv.key === radius) ?? RADIUS_LEVELS[0];
  const densityLevel = DENSITY_LEVELS.find((lv) => lv.key === density) ?? DENSITY_LEVELS[0];
  const landingLevel = LANDING_LEVELS.find((lv) => lv.key === landing) ?? LANDING_LEVELS[0];
  const widthLevel = WIDTH_LEVELS.find((lv) => lv.key === width) ?? WIDTH_LEVELS[0];
  const motionLevel = MOTION_LEVELS.find((lv) => lv.key === motion) ?? MOTION_LEVELS[0];
  const menuExpandLevel = MENU_EXPAND_LEVELS.find((lv) => lv.key === menuExpand) ?? MENU_EXPAND_LEVELS[0];
  const timeFmtLevel = TIME_FMT_LEVELS.find((lv) => lv.key === timeFmt) ?? TIME_FMT_LEVELS[0];
  const ringEnabledLevel = getRingEnabledLevel(ringEnabled);
  const ringVolumeLevel = getRingVolumeLevel(ringVolume);
  // 「常开分组」候选 = 侧栏的一级分组（与侧栏同源：registry/features 的 flatNav），不手抄分组名
  const navGroups = useMemo(() => flatNav(), []);
  // 全默认时「恢复默认设置」无事可做：置灰，而不是让用户点了没反应
  const atDefault =
    key === DEFAULT_THEME_KEY &&
    border === DEFAULT_BORDER_LEVEL &&
    width === DEFAULT_WIDTH &&
    motion === DEFAULT_MOTION &&
    radius === DEFAULT_RADIUS &&
    density === DEFAULT_DENSITY &&
    landing === DEFAULT_LANDING &&
    menuExpand === DEFAULT_MENU_EXPAND &&
    timeFmt === DEFAULT_TIME_FMT &&
    ringEnabled === DEFAULT_RING_ENABLED &&
    ringVolume === DEFAULT_RING_VOLUME;

  const resetAll = () => {
    setTheme(DEFAULT_THEME_KEY);
    setBorder(DEFAULT_BORDER_LEVEL);
    setWidth(DEFAULT_WIDTH);
    setMotion(DEFAULT_MOTION);
    setRadius(DEFAULT_RADIUS);
    setDensity(DEFAULT_DENSITY);
    setLanding(DEFAULT_LANDING);
    setMenuExpand(DEFAULT_MENU_EXPAND);
    setTimeFmt(DEFAULT_TIME_FMT);
    setRingEnabled(DEFAULT_RING_ENABLED);
    setRingVolume(DEFAULT_RING_VOLUME);
  };

  return (
    <Drawer
      width={DRAWER_WIDTH.compact}
      title="界面设置"
      visible={visible}
      onCancel={closeDrawer}
      footer={
        <div className="bcc-theme-foot">
          <Button disabled={atDefault} onClick={resetAll}>
            恢复默认设置
          </Button>
          <Button type="primary" onClick={closeDrawer}>
            完成
          </Button>
        </div>
      }
    >
      {/* headerPadding={false}：Arco 给页签首项 16px 左缩进的兜底开关（不加则页签比标题与内容右缩一格） */}
      <BccTabs activeTab={tab} onChange={setTab} contentPadding={false} headerPadding={false}>
        <BccTabPane key="theme" title="主题配色">
          <div className="bcc-theme-body">
            <Radio.Group
              className="bcc-theme-list"
              direction="vertical"
              value={key}
              onChange={(v) => setTheme(String(v))}
            >
              {THEMES.map((t) => (
                <Radio key={t.key} value={t.key} className="bcc-theme-radio">
                  <div className="bcc-theme-row" data-active={t.key === key ? '1' : undefined}>
                    <b className="bcc-theme-row-name">{t.name}</b>
                    <i className="bcc-theme-strip" aria-hidden style={themeStrip(t)} />
                  </div>
                </Radio>
              ))}
            </Radio.Group>
            <div className="bcc-theme-summary">
              <div className="bcc-theme-summary-head">
                <span className="bcc-theme-summary-cur">当前：{current.name}</span>
                <span className="bcc-theme-summary-en">{current.en}</span>
              </div>
              <p className="bcc-theme-summary-scene">{current.scene}</p>
            </div>
            <div className="bcc-theme-note">主题保存在本机浏览器，切换即时生效；通话中的工作台不会因换主题中断。</div>
          </div>
        </BccTabPane>
        <BccTabPane key="border" title="线框样式">
          <div className="bcc-theme-body">
            <div className="bcc-pref-row">
              <div className="bcc-pref-head">
                <span className="bcc-pref-title">边界强度</span>
                <Radio.Group
                  className="bcc-pref-seg"
                  type="button"
                  size="small"
                  value={border}
                  onChange={(v) => setBorder(String(v))}
                >
                  {BORDER_LEVELS.map((lv) => (
                    <Radio key={lv.key} value={lv.key}>
                      {lv.name}
                    </Radio>
                  ))}
                </Radio.Group>
              </div>
              <div className="bcc-pref-desc">{borderLevel.desc}</div>
            </div>
            {/* 预览样本三行同起同止：标签列定宽、样本撑满余宽；描边取**当前选中档**
                （--bcc-border-preview-* 由 applyTheme 按主题派生） */}
            <div className="bcc-border-demo" data-level={border}>
              <div className="bcc-border-demo-row">
                <span className="bcc-border-demo-label">输入框</span>
                <div className="bcc-border-demo-field">请输入关键字</div>
              </div>
              <div className="bcc-border-demo-row">
                <span className="bcc-border-demo-label">卡片</span>
                <div className="bcc-border-demo-card">
                  <b>卡片标题</b>
                  <span>卡片与表格的描边属于装饰边界，档位只调轻重</span>
                </div>
              </div>
              <div className="bcc-border-demo-row">
                <span className="bcc-border-demo-label">表格</span>
                <div className="bcc-border-demo-table">
                  <span>表头</span>
                  <span>单元格</span>
                  <span>单元格</span>
                </div>
              </div>
            </div>
            <div className="bcc-theme-note">线框样式保存在本机浏览器，切换即时生效。</div>
          </div>
        </BccTabPane>
        <BccTabPane key="prefs" title="界面偏好">
          <div className="bcc-theme-body">
            <PrefRow
              title="圆角"
              value={radius}
              options={RADIUS_LEVELS}
              desc={radiusLevel.desc}
              onChange={setRadius}
            />
            <PrefRow
              title="内容宽度"
              value={width}
              options={WIDTH_LEVELS}
              desc={widthLevel.desc}
              onChange={setWidth}
            />
            <PrefRow
              title="动效"
              value={motion}
              options={MOTION_LEVELS}
              desc={motionLevel.desc}
              onChange={setMotion}
            />
            <PrefRow
              title="表格密度"
              value={density}
              options={DENSITY_LEVELS}
              desc={densityLevel.desc}
              onChange={setDensity}
            />
            <PrefRow
              title="登录落点"
              value={landing}
              options={LANDING_LEVELS}
              desc={landingLevel.desc}
              onChange={setLanding}
            />
            <PrefRow
              title="侧栏展开"
              value={menuExpand}
              options={MENU_EXPAND_LEVELS}
              desc={menuExpandLevel.desc}
              onChange={setMenuExpand}
            />
            <PrefRow
              title="时间显示"
              value={timeFmt}
              options={TIME_FMT_LEVELS}
              desc={timeFmtLevel.desc}
              onChange={setTimeFmt}
            />
            {/* 「常开分组」只在选定该档时展开：另外两档下这份勾选不生效，显示出来只会让人以为开关失灵 */}
            {menuExpand === 'pinned' && (
              <div className="bcc-pref-pins">
                <Checkbox.Group
                  value={menuPinned}
                  onChange={(v) => setMenuPinned(v as string[])}
                  direction="vertical"
                >
                  {navGroups.map((g) => (
                    <Checkbox key={g.key} value={g.key}>
                      {g.title}
                    </Checkbox>
                  ))}
                </Checkbox.Group>
              </div>
            )}
            <div className="bcc-theme-note">界面偏好保存在本机浏览器，切换即时生效；「恢复默认设置」会一并复位主题、线框、界面偏好与提醒。</div>
          </div>
        </BccTabPane>
        <BccTabPane key="ring" title="提醒">
          <div className="bcc-theme-body">
            <PrefRow
              title="来电铃声"
              value={ringEnabled}
              options={RING_ENABLED_LEVELS}
              desc={ringEnabledLevel.desc}
              onChange={setRingEnabled}
            />
            <PrefRow
              title="提醒音量"
              value={ringVolume}
              options={RING_VOLUME_LEVELS}
              desc={ringVolumeLevel.desc}
              onChange={setRingVolume}
            />
            <div className="bcc-theme-note">
              提醒偏好保存在本机浏览器，切换即时生效；音量作用于来电振铃与高风险提醒的提示音，不影响通话音量。
            </div>
          </div>
        </BccTabPane>
      </BccTabs>
    </Drawer>
  );
}
